/**
 * File: ContractAnalysisComparisonDashboard.jsx
 * Path: /src/components/solutions/ContractAnalysisComparisonDashboard.jsx
 * Description: Standalone risk comparison dashboard for Contract Analysis AI solution
 * Key Features: Visualization of risk distribution across contracts with comparative metrics
 */

import React from 'react';
import { motion } from 'framer-motion';

const ContractAnalysisComparisonDashboard = () => {
  return (
    <div className="relative rounded-xl overflow-hidden shadow-xl w-full">
      <div className="bg-gradient-to-br from-primary-100 to-secondary-100 p-1">
        <div className="bg-white rounded-lg p-6 relative">
          {/* Document Analysis Visualization */}
          <div className="absolute inset-0 opacity-5">
            <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <pattern id="document-pattern" width="20" height="20" patternUnits="userSpaceOnUse">
                  <path d="M 20 0 L 0 0 0 20" fill="none" stroke="currentColor" strokeWidth="0.5" />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#document-pattern)" />
            </svg>
          </div>
          
          {/* Dashboard container with min-height to prevent cutting off */}
          <div className="min-h-[440px] w-full flex items-center justify-center relative z-10">
            <div className="w-full">
              {/* Mockup content */}
              <div className="flex flex-col h-full relative z-10">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-secondary-500 rounded-md flex items-center justify-center mr-3">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <span className="font-semibold text-dark-600">Risk Comparison Dashboard</span>
                  </div>
                  <div className="flex space-x-3">
                    <div className="py-1 px-3 text-xs bg-secondary-50 text-secondary-600 rounded-full">Analyzing</div>
                    <div className="py-1 px-3 text-xs bg-green-50 text-green-600 rounded-full">15 Contracts</div>
                  </div>
                </div>
                
                {/* Risk comparison visualization */}
                <div className="flex-grow grid grid-cols-6 gap-4">
                  <div className="col-span-2 border border-dark-100 rounded-lg p-4 text-sm text-dark-700">
                    <div className="text-base font-bold mb-3">Risk Categories</div>
                    <ul className="space-y-2">
                      <li className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="w-3 h-3 bg-red-500 rounded-full mr-2"></div>
                          <span>Termination</span>
                        </div>
                        <div className="text-xs font-medium">12 contracts</div>
                      </li>
                      <li className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="w-3 h-3 bg-orange-500 rounded-full mr-2"></div>
                          <span>Liability</span>
                        </div>
                        <div className="text-xs font-medium">9 contracts</div>
                      </li>
                      <li className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="w-3 h-3 bg-yellow-500 rounded-full mr-2"></div>
                          <span>Auto-renewal</span>
                        </div>
                        <div className="text-xs font-medium">7 contracts</div>
                      </li>
                      <li className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                          <span>Payment Terms</span>
                        </div>
                        <div className="text-xs font-medium">5 contracts</div>
                      </li>
                      <li className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                          <span>Confidentiality</span>
                        </div>
                        <div className="text-xs font-medium">3 contracts</div>
                      </li>
                    </ul>
                  </div>
                  
                  <div className="col-span-4 border border-dark-100 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-3">
                      <div className="text-base font-bold">Risk Distribution</div>
                      <div className="flex space-x-2 text-xs">
                        <div className="py-0.5 px-2 bg-secondary-50 text-secondary-600 rounded">By Category</div>
                        <div className="py-0.5 px-2 bg-gray-100 text-gray-600 rounded">By Vendor</div>
                      </div>
                    </div>
                    
                    {/* Bar chart visualization */}
                    <div className="h-32 flex items-end space-x-6 mb-2 px-4">
                      <div className="flex-1 flex flex-col items-center">
                        <div className="h-full flex items-end w-full">
                          <div className="w-full bg-red-500 h-3/4 rounded-t"></div>
                        </div>
                        <div className="w-3 h-0.5 bg-dark-300 my-1"></div>
                        <div className="text-xs text-dark-500">Term.</div>
                      </div>
                      <div className="flex-1 flex flex-col items-center">
                        <div className="h-full flex items-end w-full">
                          <div className="w-full bg-orange-500 h-2/3 rounded-t"></div>
                        </div>
                        <div className="w-3 h-0.5 bg-dark-300 my-1"></div>
                        <div className="text-xs text-dark-500">Liab.</div>
                      </div>
                      <div className="flex-1 flex flex-col items-center">
                        <div className="h-full flex items-end w-full">
                          <div className="w-full bg-yellow-500 h-1/2 rounded-t"></div>
                        </div>
                        <div className="w-3 h-0.5 bg-dark-300 my-1"></div>
                        <div className="text-xs text-dark-500">Renewal</div>
                      </div>
                      <div className="flex-1 flex flex-col items-center">
                        <div className="h-full flex items-end w-full">
                          <div className="w-full bg-green-500 h-1/3 rounded-t"></div>
                        </div>
                        <div className="w-3 h-0.5 bg-dark-300 my-1"></div>
                        <div className="text-xs text-dark-500">Payment</div>
                      </div>
                      <div className="flex-1 flex flex-col items-center">
                        <div className="h-full flex items-end w-full">
                          <div className="w-full bg-blue-500 h-1/5 rounded-t"></div>
                        </div>
                        <div className="w-3 h-0.5 bg-dark-300 my-1"></div>
                        <div className="text-xs text-dark-500">Confid.</div>
                      </div>
                    </div>
                    
                    <div className="flex justify-between text-xs text-dark-400 mt-4">
                      <div>Showing 15 contracts analyzed on May 4, 2025</div>
                      <div className="text-secondary-600 font-medium">View Detailed Report →</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContractAnalysisComparisonDashboard;